/**
 * What the Tasks section renders (`specs/M10-tasks.md`, `tasks/T-T1.md` §contract).
 *
 * Structurally typed, like every other module's `types.ts`: the page never imports server code,
 * so the fixture harness, the unit tests and the `/__ui/tasks` screen all run with no api and no
 * database. The shapes below are the `tasks.*` contract verbatim; when the contract changes, it
 * changes here and TypeScript finds the call sites.
 */

/* ── vocabulary (the enums the router validates against) ──────────────────── */

export const TASK_TABS = ['mine', 'assigned', 'archive'] as const;
export type TaskTab = (typeof TASK_TABS)[number];

/** The two panels round **T3** owns. They are tabs of the page, not tabs of `tasks.list`. */
export const TASK_PANELS = ['insights', 'timeline'] as const;
export type TaskPanel = (typeof TASK_PANELS)[number];

export type TaskPageTab = TaskTab | TaskPanel;

export const TASK_GROUP_BY = ['due', 'board', 'assigner', 'assignee', 'status'] as const;
export type TaskGroupBy = (typeof TASK_GROUP_BY)[number];

export const TASK_SORTS = ['due_asc', 'due_desc', 'assigned_desc', 'assigned_asc', 'name_asc'] as const;
export type TaskSort = (typeof TASK_SORTS)[number];

export const TASK_BUCKETS = ['overdue', 'today', 'this_week', 'later', 'no_date'] as const;
export type TaskBucket = (typeof TASK_BUCKETS)[number];

export const TASK_ARCHIVE_DELAYS = [1, 3, 7, 14, 30] as const;
export type TaskArchiveDelay = (typeof TASK_ARCHIVE_DELAYS)[number];

export const TASK_DIGEST_FREQUENCIES = ['off', 'daily', 'weekly'] as const;
export type TaskDigestFrequency = (typeof TASK_DIGEST_FREQUENCIES)[number];

export type TaskDoneFilter = 'any' | 'open' | 'done';

/* ── rows ─────────────────────────────────────────────────────────────────── */

export interface TaskPersonView {
  id: string;
  name: string | null;
  photoUrl: string | null;
}

export interface TaskRowView {
  id: string;
  itemId: string;
  itemName: string;
  boardId: string;
  boardName: string;
  /** Set for a subitem; the row draws the parent as monday's breadcrumb (decision 2026-09-15). */
  parentItemId: string | null;
  parentItemName: string | null;
  assignee: TaskPersonView | null;
  assigner: TaskPersonView | null;
  assignedAt: string;
  assignmentSource: string;
  statusLabel: string | null;
  /**
   * The label's colour on its own board — the live hex from the mapped status column's
   * `labels_colors` (P-267). `null` when the board maps no status column, when the label has
   * been renamed since the last sync, or when the label carries no colour; the pill falls back
   * to the empty-status grey, which is the same fallback the grid's status cell uses.
   */
  statusColor: string | null;
  isDone: boolean;
  doneAt: string | null;
  dueAt: string | null;
  bucket: TaskBucket;
  /** `false` only on the **assigned** tab: the person I delegated to cannot read the board. */
  assigneeHasAccess: boolean;
  /** The Archive tab's predicate: old-and-done, **or** archived by hand (P-301 §3). */
  archived: boolean;
  /**
   * When somebody archived this row **by hand**, or `null` (P-301 §3).
   *
   * The row menu needs the two halves apart: a row in the archive because it is old and done
   * comes back out by being reopened, one that was archived comes back out by *Unarchive*. A
   * single `archived` boolean cannot say which offer to make.
   */
  archivedAt: string | null;
}

export interface TaskGroupView {
  key: string;
  label: string;
  count: number;
  rows: TaskRowView[];
}

export interface TaskSettingsView {
  archiveDelayDays: TaskArchiveDelay;
  digestFrequency: TaskDigestFrequency;
  digestHour: number;
  remindOverdue: boolean;
  notifyOnComplete: boolean;
  tab: TaskTab;
  groupBy: TaskGroupBy;
  showArchived: boolean;
}

export interface TaskListResultView {
  groups: TaskGroupView[];
  rows: TaskRowView[];
  nextCursor: string | null;
  total: number;
  settings: TaskSettingsView;
}

/**
 * One group with its **own** page and its **own** cursor — `tasks.listByGroup` / `tasks.listGroup`
 * (**P-318**).
 *
 * The flat `tasks.list` pages the tab globally: 50 rows in `(group, sort)` order, which on a
 * 638-row tab is 50 rows of one bucket and nothing under *No date · 583* — below a header that
 * counts the rows it is not showing. `count` is still the group's size in the whole tab (P-271);
 * what is new is that `rows` is **this group's** page and `nextCursor` is **this group's** cursor,
 * so every group starts full and grows on its own.
 */
export interface TaskGroupPageView extends TaskGroupView {
  nextCursor: string | null;
}

export interface TaskListByGroupResultView {
  groups: TaskGroupPageView[];
  /** The whole tab under these filters — the N of the footer's "Showing n of N". */
  total: number;
  settings: TaskSettingsView;
}

export interface TaskCountsView {
  mine: number;
  /** Open rows of My Tasks — the rail badge (a done row kept for the archive delay is in `mine` only). */
  mineOpen: number;
  assigned: number;
  archive: number;
  overdue: number;
  dueToday: number;
  withoutAccess: number;
}

/**
 * What the filter bar may offer — `tasks.facets`, over the tab's **unfiltered** rows (P-274).
 *
 * It is a separate read from `tasks.list` on purpose: options built out of the rows on screen
 * are options the current filters already narrowed, so picking a board removed every other
 * board from the picker and the first page decided what was pickable at all.
 */
export interface TaskFacetsView {
  workspaces: { id: string; name: string }[];
  boards: { id: string; name: string; workspaceId: string }[];
  assignees: { id: string; name: string }[];
  assigners: { id: string; name: string }[];
  statusLabels: string[];
}

/* ── inputs ───────────────────────────────────────────────────────────────── */

export interface TaskFiltersInput {
  workspaceIds?: string[];
  boardIds?: string[];
  assigneeIds?: string[];
  assignerIds?: string[];
  statusLabels?: string[];
  done?: TaskDoneFilter;
  dueFrom?: string;
  dueTo?: string;
  /** The **assigned-on** window, `[assignedFrom, assignedTo)` — P-301 §1. */
  assignedFrom?: string;
  assignedTo?: string;
  query?: string;
  includeArchived?: boolean;
}

export interface TaskListInput {
  tab?: TaskTab;
  groupBy?: TaskGroupBy;
  filters?: TaskFiltersInput;
  sort?: TaskSort;
  cursor?: string | null;
  limit?: number;
}

/**
 * P-318 · the per-group read's input. It is `TaskListInput` **without** the cursor: a group's
 * cursor belongs to the group, so there is no single position for one cursor to name.
 */
export type TaskGroupedListInput = Omit<TaskListInput, 'cursor'>;

/** P-318 · the continuation of one group: its key, and that group's own cursor. */
export type TaskGroupPageInput = TaskGroupedListInput & {
  groupKey: string;
  cursor?: string | null;
};

/** The board's task mapping — which column plays which role (`specs/M10-tasks.md` §2). */
export interface TaskMappingView {
  boardId: string;
  assigneeColumnIds: string[];
  statusColumnId: string | null;
  dueColumnId: string | null;
  /** Status label **ids**, as strings — never label text (§2.3). */
  doneLabels: string[];
  overridden: boolean;
  overriddenBy: string | null;
  detectedAt: string | null;
}

/**
 * What the Preferences mapping panel may **offer** — `tasks.mapping.options` (P-294).
 *
 * Exactly the set `tasks.mapping.set` accepts, so a choice the panel shows can never be refused
 * for being the wrong kind of column. The status labels come with the column because label ids
 * are per column: the done-label picker is meaningless without the column it belongs to.
 */
export interface TaskStatusLabelOptionView {
  /** The label **id**, as a string — what `doneLabels` holds (§2.3), never the text. */
  id: string;
  text: string;
  color: string | null;
  /** `true` when monday's own `done_colors` nominates it — where the detector starts. */
  isMondayDone: boolean;
}

export interface TaskColumnOptionView {
  id: string;
  title: string;
  type: string;
  /** Present only on a status column. */
  labels?: TaskStatusLabelOptionView[];
}

export interface TaskMappingOptionsView {
  boardId: string;
  assigneeColumns: TaskColumnOptionView[];
  statusColumns: TaskColumnOptionView[];
  dueColumns: TaskColumnOptionView[];
}

/** One change to a board's mapping — `tasks.mapping.set`'s input, as the panel sends it. */
export interface TaskMappingInput {
  boardId: string;
  assigneeColumnIds?: string[];
  statusColumnId?: string | null;
  dueColumnId?: string | null;
  doneLabels?: string[];
  /** Puts the board back on the detector. */
  reset?: boolean;
}

/** One column of a board, as `columns.list` answers it — all the inline editors need. */
export interface TaskColumnView {
  boardId: string;
  id: string;
  type: string;
  title: string;
  settingsStr: Record<string, unknown>;
  width: number | null;
  position: string;
  revision: string;
}

/**
 * Everything one row's inline editors need, read in **one** permission-checked call.
 *
 * `tasks.list` carries the *rendered* task (`statusLabel`, `dueAt`), which is enough to draw a
 * row and not enough to **write** one: a people cell may hold three people and a timeline cell
 * a start date, and rewriting either from a rendered string would silently destroy what it did
 * not know about. So the editors open on the item's real cell values, fetched with
 * `boards.read` narrowed to that item (`trpc-source.ts#itemContext`).
 */
export interface TaskItemContext {
  /** `false` when the item did not come back — the row then offers the item card instead. */
  found: boolean;
  columns: TaskColumnView[];
  /** Canonical cell values, keyed by column id. */
  values: Record<string, unknown>;
  people: TaskPersonView[];
}

/**
 * What one `cells.bulkSet` answers with (P-293).
 *
 * `refused` is the api's per-item permission answer: on an `assignee` board the service now
 * writes the rows this person may write and **names** the ones it left out, instead of refusing
 * the whole board. Both fields are optional so a source that only writes (the fixture, a test
 * double) does not have to invent them.
 */
export interface BulkCellWriteResultView {
  changed?: number;
  refused?: { itemId: string; itemName: string }[];
}

/**
 * Everything the page can ask for. A source is injected, exactly like `MyWorkSource`, so the
 * page renders in the fixture harness and in jsdom with no tRPC client anywhere.
 */
export interface TasksSource {
  /**
   * The flat, globally paged list. Kept for one release (`specs/M10-tasks.md` §5.4) — the digest
   * and the two panels read through the same service — but **not** what the list page uses any
   * more: see {@link TasksSource.listByGroup}.
   */
  list(input: TaskListInput): Promise<TaskListResultView>;
  /** P-318 · every group of the tab with its own first page and its own cursor. */
  listByGroup(input: TaskGroupedListInput): Promise<TaskListByGroupResultView>;
  /** P-318 · one more page of **one** group — the "Show more" under one group header. */
  listGroup(input: TaskGroupPageInput): Promise<TaskGroupPageView>;
  counts(): Promise<TaskCountsView>;
  facets(input: { tab: TaskTab; includeArchived: boolean }): Promise<TaskFacetsView>;
  settings(): Promise<TaskSettingsView>;
  saveSettings(patch: Partial<TaskSettingsView>): Promise<TaskSettingsView>;
  nudge(input: { itemId: string; assigneeId: string; message?: string }): Promise<{ updateId: string }>;
  mapping(input: { boardId: string }): Promise<TaskMappingView>;
  /** P-294 — what the Preferences mapping panel may offer for one board. */
  mappingOptions(input: { boardId: string }): Promise<TaskMappingOptionsView>;
  /** P-294 — `tasks.mapping.set`; re-derives the board's tasks in the same transaction. */
  setMapping(input: TaskMappingInput): Promise<TaskMappingView>;
  itemContext(input: { boardId: string; itemId: string; itemName: string }): Promise<TaskItemContext>;
  setCell(input: { boardId: string; itemId: string; columnId: string; value: unknown }): Promise<unknown>;
  bulkSetCell(input: {
    boardId: string;
    itemIds: string[];
    columnId: string;
    value: unknown;
  }): Promise<BulkCellWriteResultView>;
  /**
   * **Manual archive** (P-301 §3) — `tasks.archive` / `tasks.unarchive`.
   *
   * They take **item** ids and not row ids, because the permission is the row's and the server
   * is the only thing that may decide which rows of an item this caller holds: a row it may not
   * touch comes back in `skipped` rather than refusing the whole call.
   */
  archiveTasks(input: { itemIds: string[] }): Promise<TaskArchiveResultView>;
  unarchiveTasks(input: { itemIds: string[] }): Promise<TaskArchiveResultView>;
  /**
   * **P-305** — the list's column widths, per user: `preferences.getAll` /
   * `preferences.setMany` on the `tasks.columnWidths` key.
   *
   * They are on this port and not on `tasks.settings` because they are not a Tasks *setting*:
   * `tasks.settings.set` is the pane of §5 (the archive delay, the digest, the grouping), and a
   * width is a per-browser-session gesture written on pointer-up. Going through the preferences
   * procedures keeps it one row in `user_preferences` that the fixture harness can stand in for
   * with no api at all.
   */
  columnWidths(): Promise<TaskColumnWidthsView>;
  saveColumnWidths(widths: TaskColumnWidthsView): Promise<TaskColumnWidthsView>;
}

/**
 * P-305 · the `tasks.columnWidths` preference, on the wire: `{ [tab]: { [column]: px } }`.
 *
 * Deliberately **not** typed as `Record<TaskTab, Record<TaskColumnKey, number>>`: this is a value
 * a client wrote and a client reads back, so it is validated on the way in
 * (`column-widths.ts#coerceTaskColumnWidths`) rather than trusted by its type.
 */
export type TaskColumnWidthsView = Record<string, Record<string, number>>;

/** What one `tasks.archive` / `tasks.unarchive` answers (P-301 §3). */
export interface TaskArchiveResultView {
  /** Rows changed — a two-assignee item this person holds twice counts twice. */
  changed: number;
  /** Item ids that carried no row this caller may archive. */
  skipped: string[];
}

/**
 * **P-319** · the two ids every "open this item" gesture carries.
 *
 * `onOpenItem` used to take a whole `TaskRowView`, which is what a list row is — and the two
 * other surfaces that open an item are not rows: the Insights panel hands over a row, the
 * Timeline panel hands over a `TaskMark`. Both carry these two fields and nothing else matters,
 * so this is what the page's own handler asks for; a `TaskRowView` still satisfies it
 * structurally, so no existing caller changed.
 */
export interface TaskItemRef {
  itemId: string;
  boardId: string;
}
